import { Injectable } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { format, parseISO } from "date-fns";
import { ROUTES } from "src/app/utils/routes";
import { TransferInventoryWorkflowStatus } from "src/app/utils/transfer-inventory-workflow-status";
import { decimalValidator } from "src/app/utils/validators";
import { StorageService } from "../storage.service";
import {
  TransferInventoryDetModel,
  TransferInventoryInsertModel,
  TransferInventoryModel,
  TransferInventoryUpdateModel,
} from "../swagger";

export const transferInvCreateDraftKey = "transferInvDraft:create";
export const transferInvUpdateDraftKeyPrefix = "transferInvDraft:update:";

/**
 * Drop every transfer-inventory draft on the device — used when the user
 * abandons the page and when the session ends, so a shared tablet never offers
 * one user's unsaved transfer to the next.
 */
export const clearAllTransferInvDrafts = async (
  storage: StorageService,
): Promise<void> => {
  try {
    await storage.remove(transferInvCreateDraftKey);
    const keys = await storage.keys();
    const updateKeys = (keys ?? []).filter((key) =>
      key.startsWith(transferInvUpdateDraftKeyPrefix),
    );
    for (const key of updateKeys) {
      await storage.remove(key);
    }
  } catch (e) {
    // Ignore storage errors (quota, private mode, etc.).
  }
};

export type TransferInventoryDraft = {
  meta: {
    transferInventoryId: number | null;
    transferInventoryNumber: string;
    savedAt: string;
  };
  form: any;
};

@Injectable()
export class CreateTransferInventoryForm {
  form: FormGroup;
  resource: TransferInventoryModel;
  formattedDate = "";
  dateValue = format(new Date(), "yyyy-MM-dd");
  someThingChange = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private storageService: StorageService,
  ) {}

  get lines(): FormArray {
    return this.form.get("detail") as FormArray;
  }

  get activeLines() {
    return this.lines.controls.filter((c) => c.value.statusId !== 3);
  }

  get isReadonly(): boolean {
    const ws = this.form?.value?.workflowStatusId;
    return (
      ws === TransferInventoryWorkflowStatus.Submitted ||
      ws === TransferInventoryWorkflowStatus.Voided
    );
  }

  initializeForm(model?: TransferInventoryModel) {
    this.resource = model;
    const data: any = { ...(model ?? {}) };

    this.form = this.fb.group({
      transferInventoryId: [data.transferInventoryId ?? 0],
      transferInventoryNumber: [data.transferInventoryNumber ?? ""],
      fromLocationId: [data.fromLocationId ?? null, [Validators.required]],
      fromSiteId: [data.fromSiteId ?? null, [Validators.required]],
      toLocationId: [data.toLocationId ?? null, [Validators.required]],
      toSiteId: [data.toSiteId ?? null, [Validators.required]],
      fromInvTranId: [data.fromInvTranId ?? null],
      toInvTranId: [data.toInvTranId ?? null],
      fromInvTranNumber: [data.fromInvTranNumber ?? ""],
      toInvTranNumber: [data.toInvTranNumber ?? ""],
      transferInventoryDate: [
        data.transferInventoryDate ?? null,
        [Validators.required],
      ],
      reference: [data.reference ?? ""],
      statusId: [data.statusId ?? 1, [Validators.required]],
      workflowStatusId: [
        data.workflowStatusId ?? TransferInventoryWorkflowStatus.Pending,
        [Validators.required],
      ],
      detail: this.fb.array([]),
    });

    // Date setup — mirror inv-tran's pattern (parseISO is timezone-safe and
    // avoids the "previous day" offset bug when the server stamp is a date-only
    // value or has a UTC marker).
    if (data && data.transferInventoryDate) {
      this.dateChanged(data.transferInventoryDate);
    } else {
      this.formattedDate = "";
      this.dateValue = format(new Date(), "yyyy-MM-dd");
      this.dateChanged(this.dateValue);
    }

    if (data.detail && Array.isArray(data.detail)) {
      for (const det of data.detail) {
        this.lines.push(this.buildLineGroup(det));
      }
    }
    this.someThingChange = false;

    if (this.isReadonly) {
      this.form.disable({ emitEvent: false });
    }
  }

  /**
   * Update the date controls. Mirrors inv-tran's pattern:
   * - `dateValue` stores the raw string from `<ion-datetime>` (used as the
   *   `[value]` binding so the picker re-opens on the same date).
   * - The reactive form control holds the same raw string (sent to the API).
   * - `formattedDate` is rendered as "MMM dd yyyy" via `parseISO`, which is
   *   timezone-safe (no off-by-one-day issue).
   */
  dateChanged(value: string) {
    if (!value) return;
    this.dateValue = value;
    this.form.controls.transferInventoryDate.setValue(value);
    this.form.controls.transferInventoryDate.updateValueAndValidity();
    this.formattedDate = format(parseISO(value), "MMM dd yyyy");
    this.someThingChange = true;
  }

  private buildLineGroup(det: Partial<TransferInventoryDetModel> & { itemDescription?: string | null } = {}): FormGroup {
    return this.fb.group({
      transferInventoryDetId: [det.transferInventoryDetId ?? 0],
      transferInventoryId: [det.transferInventoryId ?? 0],
      ordinal: [det.ordinal ?? 0, [Validators.required]],
      itemNumber: [det.itemNumber ?? "", [Validators.required]],
      itemDescription: [det.itemDescription ?? ""],
      uOfM: [det.uOfM ?? "", [Validators.required]],
      units: [
        det.units ?? null,
        [Validators.required, decimalValidator({ required: true, min: 0 })],
      ],
      quantity: [det.quantity ?? 1, [Validators.required]],
      unitPrice: [det.unitPrice ?? null],
      amount: [det.amount ?? null],
      reference: [det.reference ?? ""],
      uniqueIdentifierType: [det.uniqueIdentifierType ?? null],
      uniqueIdentifier: [det.uniqueIdentifier ?? ""],
      batchIdentifierType: [det.batchIdentifierType ?? null],
      batchIdentifier: [det.batchIdentifier ?? ""],
      sellByDate: [det.sellByDate ?? null],
      barcodeSource: [det.barcodeSource ?? ""],
      timeStamp: [det.timeStamp ?? new Date().toISOString()],
      statusId: [det.statusId ?? 1, [Validators.required]],
    });
  }

  addLine(payload: Partial<TransferInventoryDetModel> = {}) {
    const nextOrdinal =
      this.activeLines.reduce(
        (max, c) => Math.max(max, Number(c.value.ordinal) || 0),
        0,
      ) + 1;
    this.lines.push(this.buildLineGroup({ ...payload, ordinal: nextOrdinal }));
    this.someThingChange = true;
  }

  updateLine(index: number, payload: Partial<TransferInventoryDetModel>) {
    const ctrl = this.lines.at(index) as FormGroup;
    if (!ctrl) return;
    ctrl.patchValue(payload);
    this.someThingChange = true;
  }

  removeLine(index: number) {
    const ctrl = this.lines.at(index);
    if (!ctrl) return;
    const detId = ctrl.value.transferInventoryDetId;
    if (!detId) {
      this.lines.removeAt(index);
    } else {
      ctrl.patchValue({ statusId: 3 });
    }
    this.someThingChange = true;
  }

  getInsertPayload(workflowStatusId: TransferInventoryWorkflowStatus): TransferInventoryInsertModel {
    const v = this.form.getRawValue();
    return {
      fromLocationId: v.fromLocationId,
      fromSiteId: v.fromSiteId,
      toLocationId: v.toLocationId,
      toSiteId: v.toSiteId,
      transferInventoryDate: v.transferInventoryDate,
      reference: v.reference,
      workflowStatusId,
      detail: this.activeLines.map((c) => {
        // For insert, strip the IDs (server assigns them).
        const { transferInventoryDetId, transferInventoryId, ...rest } = c.value;
        return rest as TransferInventoryDetModel;
      }),
    };
  }

  getUpdatePayload(workflowStatusId: TransferInventoryWorkflowStatus): TransferInventoryUpdateModel {
    const v = this.form.getRawValue();
    return {
      transferInventoryId: v.transferInventoryId,
      transferInventoryNumber: v.transferInventoryNumber,
      fromLocationId: v.fromLocationId,
      fromSiteId: v.fromSiteId,
      fromInvTranId: v.fromInvTranId,
      toLocationId: v.toLocationId,
      toSiteId: v.toSiteId,
      toInvTranId: v.toInvTranId,
      transferInventoryDate: v.transferInventoryDate,
      reference: v.reference,
      statusId: v.statusId,
      workflowStatusId,
      // Spec also expects these fields on Update. Pass through what the
      // server gave us (read-only, for echo).
      fromInvTranNumber: v.fromInvTranNumber ?? "",
      toInvTranNumber: v.toInvTranNumber ?? "",
      statusName: this.resource?.statusName ?? "Active",
      detail: (this.lines.controls as FormGroup[]).map(
        (c) => c.getRawValue() as TransferInventoryDetModel,
      ),
    } as TransferInventoryUpdateModel;
  }

  markUnsaved() {
    this.someThingChange = true;
  }

  markSaved() {
    this.someThingChange = false;
    // `dirty` is the other half of the unsaved-change signal (see
    // hasUnsavedChanges) — leaving it set would keep the draft autosave firing
    // and the leave-page guard tripping after a successful save.
    this.form?.markAsPristine();
  }

  /**
   * Single source of truth for "the user has unsaved work".
   *
   * Two independent signals feed it: controls edited through a value accessor
   * set `dirty`, while controls updated programmatically via patchValue don't,
   * and their handlers call markUnsaved() instead. Both the draft autosave and
   * the leave-page guard must read the same combination, or one fires without
   * the other and drafts get written for edits the page never warns about.
   */
  get hasUnsavedChanges(): boolean {
    return this.someThingChange || !!this.form?.dirty;
  }

  // ---- Local draft autosave (device storage) -----------------------------

  async getCreateDraft(): Promise<TransferInventoryDraft | null> {
    return await this.readDraft(transferInvCreateDraftKey);
  }

  async getUpdateDraft(
    transferInventoryId: number,
  ): Promise<TransferInventoryDraft | null> {
    if (!transferInventoryId) return null;
    return await this.readDraft(this.getUpdateDraftKey(transferInventoryId));
  }

  async saveDraftFromForm(): Promise<void> {
    if (!this.form) return;
    const formValue = this.form.getRawValue();
    const transferInventoryId = this.currentRecordId();
    const draft: TransferInventoryDraft = {
      meta: {
        transferInventoryId,
        transferInventoryNumber: formValue?.transferInventoryNumber ?? "",
        savedAt: new Date().toISOString(),
      },
      form: formValue,
    };
    const key = transferInventoryId
      ? this.getUpdateDraftKey(transferInventoryId)
      : transferInvCreateDraftKey;
    await this.writeDraft(key, draft);
  }

  async clearDraftForCurrentForm(): Promise<void> {
    if (!this.form) return;
    const transferInventoryId = this.currentRecordId();
    if (transferInventoryId) {
      await this.clearDraft(this.getUpdateDraftKey(transferInventoryId));
    } else {
      await this.clearDraft(transferInvCreateDraftKey);
    }
  }

  restoreFromDraft(draft: TransferInventoryDraft) {
    if (!draft?.form || !this.form) return;
    // A locked record is showing values that were already posted. Patching a
    // stale draft over them would hide what is actually saved, in fields the
    // user cannot correct because the form is disabled.
    if (this.isReadonly) return;
    const f = draft.form;
    this.lines.clear();
    if (Array.isArray(f.detail)) {
      for (const det of f.detail) {
        this.lines.push(this.buildLineGroup(det));
      }
    }
    const { detail, ...header } = f;
    this.form.patchValue(header);
    if (f.transferInventoryDate) {
      this.dateChanged(f.transferInventoryDate);
    }
    this.someThingChange = true;
  }

  /**
   * The id of the record being edited, or null for one that has never been
   * saved. A new transfer carries id 0 rather than null, so a bare truthiness
   * check is what keeps it off a "transferInvDraft:update:0" key.
   */
  private currentRecordId(): number | null {
    const id = this.form?.getRawValue()?.transferInventoryId;
    return id ? id : null;
  }

  private getUpdateDraftKey(transferInventoryId: number): string {
    return `${transferInvUpdateDraftKeyPrefix}${transferInventoryId}`;
  }

  private async readDraft(
    key: string,
  ): Promise<TransferInventoryDraft | null> {
    try {
      const raw = await this.storageService.get(key);
      if (!raw) return null;
      return JSON.parse(raw) as TransferInventoryDraft;
    } catch (e) {
      return null;
    }
  }

  private async writeDraft(
    key: string,
    draft: TransferInventoryDraft,
  ): Promise<void> {
    try {
      await this.storageService.set(key, JSON.stringify(draft));
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }

  private async clearDraft(key: string): Promise<void> {
    try {
      await this.storageService.remove(key);
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }

  async backToOperationsPage() {
    await this.router.navigate([ROUTES.OPERATIONS_TRANSFER_INVENTORY_PAGE]);
  }
}
